const getDefaultDocument = () =>
  typeof document !== 'undefined' ? document : null;

const getDefaultNavigator = () =>
  typeof navigator !== 'undefined' ? navigator : null;

const getDefaultWindow = () => (typeof window !== 'undefined' ? window : null);

const isNavigatorOnline = (navigatorObject = getDefaultNavigator()) => {
  try {
    return navigatorObject?.onLine !== false;
  } catch (_) {
    return true;
  }
};

const safeConfirm = (
  message,
  { defaultValue = false, windowObject = getDefaultWindow() } = {}
) => {
  try {
    if (typeof windowObject?.confirm !== 'function') {
      return defaultValue;
    }

    return Boolean(windowObject.confirm(message));
  } catch (_) {
    return defaultValue;
  }
};

const safePrompt = (
  message,
  { defaultValue = null, initialValue, windowObject = getDefaultWindow() } = {}
) => {
  try {
    if (typeof windowObject?.prompt !== 'function') {
      return defaultValue;
    }

    const value =
      initialValue === undefined
        ? windowObject.prompt(message)
        : windowObject.prompt(message, initialValue);
    return value == null ? defaultValue : value;
  } catch (_) {
    return defaultValue;
  }
};

const safeAlert = (message, { windowObject = getDefaultWindow() } = {}) => {
  try {
    if (typeof windowObject?.alert !== 'function') {
      return false;
    }

    windowObject.alert(message);
    return true;
  } catch (_) {
    return false;
  }
};

const isMediaQueryMatching = (
  query,
  { defaultValue = false, windowObject = getDefaultWindow() } = {}
) => {
  try {
    if (!query || typeof windowObject?.matchMedia !== 'function') {
      return defaultValue;
    }

    return Boolean(windowObject.matchMedia(query)?.matches);
  } catch (_) {
    return defaultValue;
  }
};

const safeGetMediaQueryList = (
  query,
  { windowObject = getDefaultWindow() } = {}
) => {
  try {
    if (!query || typeof windowObject?.matchMedia !== 'function') {
      return null;
    }

    return windowObject.matchMedia(query) || null;
  } catch (_) {
    return null;
  }
};

const safeSetMediaQueryChangeHandler = (
  query,
  handler,
  { windowObject = getDefaultWindow() } = {}
) => {
  if (typeof handler !== 'function') {
    return false;
  }

  const mediaQueryList = safeGetMediaQueryList(query, { windowObject });
  if (!mediaQueryList) {
    return false;
  }

  try {
    mediaQueryList.onchange = handler;
    return true;
  } catch (_) {
    return false;
  }
};

const safeGetComputedStyle = (
  element,
  { fallback = {}, windowObject = getDefaultWindow() } = {}
) => {
  try {
    if (!element || typeof windowObject?.getComputedStyle !== 'function') {
      return fallback;
    }

    return windowObject.getComputedStyle(element) || fallback;
  } catch (_) {
    return fallback;
  }
};

const getWindowGlobal = (
  key,
  { defaultValue = undefined, windowObject = getDefaultWindow() } = {}
) => {
  try {
    if (!key || !windowObject) {
      return defaultValue;
    }

    return windowObject[key] ?? defaultValue;
  } catch (_) {
    return defaultValue;
  }
};

const setWindowGlobal = (
  key,
  value,
  { windowObject = getDefaultWindow() } = {}
) => {
  try {
    if (!key || !windowObject) {
      return false;
    }

    const target = windowObject;
    target[key] = value;
    return true;
  } catch (_) {
    return false;
  }
};

const clearWindowGlobal = (key, { windowObject = getDefaultWindow() } = {}) =>
  setWindowGlobal(key, undefined, { windowObject });

const getClipboardItemConstructor = (windowObject = getDefaultWindow()) => {
  try {
    return windowObject?.ClipboardItem || null;
  } catch (_) {
    return null;
  }
};

const writeClipboardItems = async (
  items,
  navigatorObject = getDefaultNavigator()
) => {
  try {
    if (typeof navigatorObject?.clipboard?.write !== 'function') {
      return false;
    }

    await navigatorObject.clipboard.write(items);
    return true;
  } catch (_) {
    return false;
  }
};

const getCookieValue = (name, documentObject = getDefaultDocument()) => {
  if (!name) return null;

  try {
    const value = `; ${documentObject?.cookie || ''}`;
    const parts = value.split(`; ${name}=`);

    return parts.length === 2 ? parts.pop().split(';').shift() : null;
  } catch (_) {
    return null;
  }
};

export {
  clearWindowGlobal,
  getClipboardItemConstructor,
  getCookieValue,
  getWindowGlobal,
  isMediaQueryMatching,
  isNavigatorOnline,
  safeAlert,
  safeConfirm,
  safeGetComputedStyle,
  safeGetMediaQueryList,
  safePrompt,
  safeSetMediaQueryChangeHandler,
  setWindowGlobal,
  writeClipboardItems,
};
